import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import api from '../services/api';
import { getOrdersRequest } from '../store/modules/orders/actions';
import { getLayersRequest } from '../store/modules/layers/actions';
import { IOrdersInput } from '../store/modules/orders/types';
import { ILayerInput } from '../store/modules/layers/types';
import { IUser, IUserAvatar, IUnit } from '../types/users';
import { persistor } from '../store';
import { IKpisInput } from '../store/modules/kpis/types';
import { getKpisRequest } from '../store/modules/kpis/actions';

interface ILoginResponse {
  jwt: string;
  user: IUser;
}

interface AuthState {
  jwt: string;
  user: IUser;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: IUser;
  jwt: string;
  selectedUnit: IUnit;
  loading: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  selectUnit(unitId: string): void;
  updateUser(user: IUser): void;
  updateUserAvatar(userAvatar: IUserAvatar, user: IUser): void;
}

const ITABIRA_DATA: IUnit = {
  id: 'itabira',
  name: 'Complexo de Itabira',
  lat: -19.655305242111528,
  lng: -43.22048631091433,
};

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [selectedUnit, setSelectedUnit] = useState<IUnit>(ITABIRA_DATA);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStoragedData(): Promise<void> {
      const jwt = localStorage.getItem('@minerAI:jwt');
      const user = localStorage.getItem('@minerAI:user');

      if (jwt && user) {
        api.defaults.headers.authorization = `Bearer ${jwt}`;
        const userData = JSON.parse(user);

        setData({ jwt, user: userData });
        setSelectedUnit(ITABIRA_DATA);
        // setSelectedUnit(userData.units[0]);
      }

      setLoading(false);
    }

    loadStoragedData();
  }, []);

  const handleGetOrders = useCallback(
    (input: IOrdersInput) => {
      dispatch(getOrdersRequest(input));
    },
    [dispatch],
  );

  const handleGetLayers = useCallback(
    (jwt: string, user: IUser) => {
      const input: ILayerInput = {
        user,
        jwt,
      };
      dispatch(getLayersRequest(input));
    },
    [dispatch],
  );

  const handleGetKpis = useCallback(
    (input: IKpisInput) => {
      dispatch(getKpisRequest(input));
    },
    [dispatch],
  );

  const signIn = useCallback(
    async ({ email, password }) => {
      const loginData = new FormData();

      loginData.append('email', email);
      loginData.append('password', password);

      const response = await api.post<ILoginResponse>('login', loginData);

      const { jwt, user } = response.data;

      if (!user.organization.id || user.units.length === 0) {
        throw new Error('no organization/units');
      }

      if (user.active === 0) {
        throw new Error('user inactive');
      }

      if (user.access_level && user.access_level > 2) {
        throw new Error('access denied');
      }

      localStorage.setItem('@minerAI:jwt', jwt);
      localStorage.setItem('@minerAI:user', JSON.stringify(user));

      api.defaults.headers.authorization = `Bearer ${jwt}`;

      const ordersInput = {
        user,
        jwt,
        status: 'ABERTA,INCOMPLETA,FINALIZADA',
      };

      const kpisInput: IKpisInput = {
        user,
        jwt,
        timescale: 'month',
      };

      handleGetOrders(ordersInput);
      handleGetKpis(kpisInput);
      handleGetLayers(jwt, user);

      setData({ jwt, user });
    },
    [handleGetOrders, handleGetLayers, handleGetKpis],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@minerAI:jwt');
    localStorage.removeItem('@minerAI:user');
    persistor.purge();

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: IUser) => {
      localStorage.setItem('@minerAI:user', JSON.stringify(user));

      setData({
        jwt: data.jwt,
        user,
      });
    },
    [setData, data.jwt],
  );

  const updateUserAvatar = useCallback(
    (userAvatar: IUserAvatar, user: IUser) => {
      localStorage.setItem('@minerAI:user', JSON.stringify(user));

      setData({
        jwt: data.jwt,
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          avatar_url: userAvatar.avatar.url,
          active: user.active,
          first_time: user.first_time,
          user_type: user.user_type,
          access_level: user.access_level,
          units: user.units,
          organization: user.organization,
        },
      });
    },
    [setData, data.jwt],
  );

  const selectUnit = useCallback(
    (unitId: string) => {
      if (unitId === 'itabira') {
        setSelectedUnit(ITABIRA_DATA);
        return;
      }

      const unitData = data.user.units.find(unit => unit.id === unitId);

      if (!unitData) {
        return;
      }

      setSelectedUnit(unitData);
    },
    [data],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        jwt: data.jwt,
        selectedUnit,
        loading,
        signIn,
        signOut,
        updateUser,
        updateUserAvatar,
        selectUnit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
